/**
 * Manage Insights — one page, two tabs:
 *   Run analysis on games          (the default; formerly /insights-packs)
 *   Manage intelligence & visual analytics   (the registries)
 *
 * Lived at /insights-lab until 2026-09-07, when that address went to the
 * Insights Lab (AnalyticsCollaboration.tsx) to match the rail: this is the
 * management surface, that is the lab. Old addresses redirect in App.tsx.
 *
 * Staff-only. The server enforces this on every /api/v1/insights/* call;
 * the page shows a quiet notice rather than a broken screen for anyone who
 * reaches it by URL. Tab content lives in LabGames.tsx and LabRegistry.tsx.
 */
import { useEffect } from 'react'
import { Link } from 'react-router-dom'

import { type Session } from '../lib/api'
import { prefetchClubs } from '../lib/clubsCache'
import LabBuilds from './LabBuilds'
import LabGames from './LabGames'
import LabRegistry from './LabRegistry'

import './admin.css'
import './insights-lab.css'

export type LabTab = 'games' | 'registry' | 'builds'

const TABS: { tab: LabTab; path: string; label: string; sub: string }[] = [
  {
    tab: 'games',
    path: '/manage-insights/games',
    label: 'Run analysis on games',
    sub: 'Open a club to see its games with processed tracking, then open a '
      + 'game to run packs and read the reports.',
  },
  {
    tab: 'registry',
    path: '/manage-insights/registry',
    label: 'Manage insight packs',
    sub: 'Every registered insight pack: the serve tables it reads, the '
      + 'queries it runs, and where it came from. New packs and versions '
      + 'are imported from the marketplace.',
  },
  {
    tab: 'builds',
    path: '/manage-insights/builds',
    label: 'Builds',
    sub: 'Which games have a report for each pack, and what is building '
      + 'right now — the coverage grid above, the live build queue below.',
  },
]

export default function ManageInsights({ session, tab }: { session: Session; tab: LabTab }) {
  const isStaff = session.user.is_staff || session.user.is_platform_admin
  // Warm the club list the moment the page opens, so the Games tab's picker
  // is populated from cache instead of paying the admin listing on click.
  useEffect(() => {
    if (isStaff) prefetchClubs()
  }, [isStaff])
  if (!isStaff) {
    return (
      <>
        <h1 className="page-title">Manage Insights</h1>
        <p className="page-sub">Priori staff only.</p>
      </>
    )
  }
  const active = TABS.find((t) => t.tab === tab) ?? TABS[0]!
  return (
    <div className="insights-lab">
      <div className="lab-eyebrow">Manage insights · Priori staff only</div>
      <h1 className="page-title">Manage insights</h1>
      <nav className="lab-tabs" aria-label="Manage insights sections">
        {TABS.map((t) => (
          <Link key={t.tab} to={t.path}
                className={t.tab === active.tab ? 'is-on' : ''}
                aria-current={t.tab === active.tab ? 'page' : undefined}>
            {t.label}
          </Link>
        ))}
      </nav>
      <p className="lab-sub">{active.sub}</p>
      {active.tab === 'games' && <LabGames />}
      {active.tab === 'registry' && (
        <LabRegistry canInstall={!!session.user.is_platform_admin} />
      )}
      {active.tab === 'builds' && <LabBuilds />}
    </div>
  )
}
